import { label } from "../../config";

/**
 * Maps a project_variables API document to the workflow-variable shape used in
 * form builder / workflow chip lists (aligned with getWorkflowVariables entries).
 * @param {Object} doc - Raw project variable from getProjectVariables
 * @returns {Object|null}
 * @author Muskan Thakur
 */
export const mapProjectVariableDocToWorkflowVariableShape = (doc) => {
  if (!doc || typeof doc !== "object") return null;
  const id = doc._id != null ? String(doc._id) : doc.id != null ? String(doc.id) : null;
  if (!id) return null;
  return {
    id,
    name: doc.name || "",
    type: String(doc.type || "dropdown").toLowerCase(),
    attributes: doc.attributes ?? {},
    description: doc.description || "",
    chipType: label.SMART_CHIP,
    adminType: doc.adminType ?? null,
    variableId: doc.variableId ?? null,
    adminCustomVariables: doc.adminCustomVariables ?? null,
    isProjectVariable: true,
  };
};

/**
 * Appends project-scoped chips after workflow API variables, skipping duplicates by id.
 * @param {Array} workflowVariables - Variables from getWorkflowVariables
 * @param {Array} projectDocs - Documents from getProjectVariables
 * @returns {Array}
 * @author Muskan Thakur
 */
export const appendProjectVariablesToWorkflowList = (workflowVariables, projectDocs) => {
  const base = Array.isArray(workflowVariables) ? [...workflowVariables] : [];
  const seen = new Set(base.map((v) => (v?.id != null ? String(v.id) : "")).filter(Boolean));
  const docs = Array.isArray(projectDocs) ? projectDocs : [];
  for (const doc of docs) {
    const mapped = mapProjectVariableDocToWorkflowVariableShape(doc);
    if (!mapped) continue;
    if (seen.has(mapped.id)) continue;
    seen.add(mapped.id);
    base.push(mapped);
  }
  return base;
};

/**
 * Builds a synthetic editor row for a project variable (task / workflow description context).
 * @param {Object} doc - Raw project variable document
 * @param {boolean} descriptionMode - When true, sets workflowDescriptionVariableId; otherwise taskVariableId
 * @returns {Object|null}
 * @author Muskan Thakur
 */
export const mapProjectVariableDocToEditorVariableRow = (doc, descriptionMode) => {
  const details = mapProjectVariableDocToWorkflowVariableShape(doc);
  if (!details) return null;
  const id = details.id;
  const syntheticInstanceId = `pv-${id}`;
  const base = {
    workflowVariableId: id,
    workflowVariableDetails: details,
    variableName: details.name,
    variableValue: null,
  };
  if (descriptionMode) {
    return { ...base, workflowDescriptionVariableId: syntheticInstanceId };
  }
  return { ...base, taskVariableId: syntheticInstanceId };
};

/**
 * Appends project variables as editor variable rows when not already represented.
 * @param {Array} variables - Existing variables from getTaskVariables / getWorkflowDescriptionVariables
 * @param {Array} projectDocs - getProjectVariables result
 * @param {boolean} descriptionMode
 * @returns {Array}
 * @author Muskan Thakur
 */
export const appendProjectVariablesToEditorVariableList = (variables, projectDocs, descriptionMode) => {
  const list = Array.isArray(variables) ? [...variables] : [];
  const existingWfIds = new Set(
    list
      .map((v) => {
        const wid = v?.workflowVariableId ?? v?.workflowVariableDetails?.id ?? v?.workflowVariableDetails?._id;
        return wid != null ? String(wid) : "";
      })
      .filter(Boolean)
  );
  const docs = Array.isArray(projectDocs) ? projectDocs : [];
  for (const doc of docs) {
    const rowId = doc?._id != null ? String(doc._id) : doc?.id != null ? String(doc.id) : "";
    if (!rowId || existingWfIds.has(rowId)) continue;
    const row = mapProjectVariableDocToEditorVariableRow(doc, descriptionMode);
    if (!row) continue;
    existingWfIds.add(rowId);
    list.push(row);
  }
  return list;
};


export const DEFAULT_VARIABLE_ATTRIBUTES = {
  date : {
    dateFormat: {
      label: "dd/MM/yyyy",
      value: "dd/MM/yyyy",
    },
    dateSeparator: {
      label: "/",
      value: "/",
    },
  },
};